// services/appSettingsService.js
import api from "./api";

// ===== APP CONFIG MANAGEMENT =====
const createAppConfig = async (configData) => {
  const response = await api.post("/admin-app-settings/config", configData);
  return response.data;
};

const updateAppConfig = async (id, configData) => {
  const response = await api.patch(
    `/admin-app-settings/config/${id}`,
    configData
  );
  return response.data;
};

const getAllConfigs = async (filterParams) => {
  const response = await api.get("/admin-app-settings/config", {
    params: filterParams,
  });
  return response.data;
};

const getConfigById = async (id) => {
  const response = await api.get(`/admin-app-settings/config/${id}`);
  return response.data;
};

const deleteConfig = async (id) => {
  const response = await api.delete(`/admin-app-settings/config/${id}`);
  return response.data;
};

const activateConfig = async (id) => {
  const response = await api.patch(`/admin-app-settings/config/${id}/activate`);
  return response.data;
};

const cloneConfig = async (id) => {
  const response = await api.post(`/admin-app-settings/config/${id}/clone`);
  return response.data;
};

// ===== BANNER MANAGEMENT =====
const createBanner = async (bannerData) => {
  // bannerData is already a FormData object from handleSubmit
  // Just pass it directly without transformation
  const response = await api.post("/admin-app-settings/banners", bannerData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

const updateBanner = async (id, bannerData) => {
  // bannerData is already a FormData object from handleSubmit
  // Just pass it directly without transformation
  const response = await api.patch(
    `/admin-app-settings/banners/${id}`,
    bannerData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const getAllBanners = async (filterParams) => {
  const response = await api.get("/admin-app-settings/banners", {
    params: filterParams,
  });
  return response.data;
};

const getBannerById = async (id) => {
  const response = await api.get(`/admin-app-settings/banners/${id}`);
  return response.data;
};

const deleteBanner = async (id) => {
  const response = await api.delete(`/admin-app-settings/banners/${id}`);
  return response.data;
};

const updateBannerStatus = async (id, statusData) => {
  const response = await api.patch(
    `/admin-app-settings/banners/${id}/status`,
    statusData
  );
  return response.data;
};

const reorderBanners = async (orderData) => {
  const response = await api.post(
    "/admin-app-settings/banners/reorder",
    orderData
  );
  return response.data;
};

const getBannerAnalytics = async (id) => {
  const response = await api.get(`/admin-app-settings/banners/${id}/analytics`);
  return response.data;
};

// ===== COLLECTION MANAGEMENT =====
const createCollection = async (collectionData) => {
  const response = await api.post(
    "/admin-app-settings/collections",
    collectionData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const updateCollection = async (id, collectionData) => {
  const response = await api.patch(
    `/admin-app-settings/collections/${id}`,
    collectionData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const getAllCollections = async (filterParams) => {
  const response = await api.get("/admin-app-settings/collections", {
    params: filterParams,
  });
  return response.data;
};

const getCollectionById = async (id) => {
  const response = await api.get(`/admin-app-settings/collections/${id}`);
  return response.data;
};

const deleteCollection = async (id) => {
  const response = await api.delete(`/admin-app-settings/collections/${id}`);
  return response.data;
};

const updateCollectionStatus = async (id, statusData) => {
  const response = await api.patch(
    `/admin-app-settings/collections/${id}/status`,
    statusData
  );
  return response.data;
};

const getCollectionProducts = async (id, filterParams) => {
  const response = await api.get(
    `/admin-app-settings/collections/${id}/products`,
    { params: filterParams }
  );
  return response.data;
};

// ===== CONTACT INFO MANAGEMENT =====
const createContactInfo = async (contactData) => {
  const response = await api.post("/admin-app-settings/contact", contactData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

const updateContactInfo = async (id, contactData) => {
  const response = await api.patch(
    `/admin-app-settings/contact/${id}`,
    contactData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const getAllContactInfo = async (filterParams) => {
  const response = await api.get("/admin-app-settings/contact", {
    params: filterParams,
  });
  return response.data;
};

const getContactInfoById = async (id) => {
  const response = await api.get(`/admin-app-settings/contact/${id}`);
  return response.data;
};

const deleteContactInfo = async (id) => {
  const response = await api.delete(`/admin-app-settings/contact/${id}`);
  return response.data;
};

// ===== FEATURE FLAGS MANAGEMENT =====
const updateFeatureFlags = async (configId, featureData) => {
  const response = await api.patch(
    `/admin-app-settings/features/${configId}`,
    featureData
  );
  return response.data;
};

const bulkUpdateFeatures = async (featuresData) => {
  const response = await api.post(
    "/admin-app-settings/features/bulk",
    featuresData
  );
  return response.data;
};

// ===== HOME SCREEN LAYOUT =====
const updateHomeLayout = async (configId, layoutData) => {
  const response = await api.patch(
    `/admin-app-settings/home-layout/${configId}`,
    layoutData
  );
  return response.data;
};

const previewHomeLayout = async (layoutData) => {
  const response = await api.post(
    "/admin-app-settings/home-layout/preview",
    layoutData
  );
  return response.data;
};

// ===== IMPORT/EXPORT =====
const importBanners = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post(
    "/admin-app-settings/import/banners",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
};

const exportConfig = async (id) => {
  const response = await api.get(`/admin-app-settings/export/config/${id}`, {
    responseType: "blob",
  });
  return response.data;
};

// ===== A/B TESTING =====
const createExperiment = async (experimentData) => {
  const response = await api.post(
    "/admin-app-settings/experiments",
    experimentData
  );
  return response.data;
};

const updateExperiment = async (id, experimentData) => {
  const response = await api.patch(
    `/admin-app-settings/experiments/${id}`,
    experimentData
  );
  return response.data;
};

const getExperiments = async (filterParams) => {
  const response = await api.get("/admin-app-settings/experiments", {
    params: filterParams,
  });
  return response.data;
};

const getExperimentResults = async (id) => {
  const response = await api.get(
    `/admin-app-settings/experiments/${id}/results`
  );
  return response.data;
};

const appSettingsService = {
  // App Config
  createAppConfig,
  updateAppConfig,
  getAllConfigs,
  getConfigById,
  deleteConfig,
  activateConfig,
  cloneConfig,

  // Banners
  createBanner,
  updateBanner,
  getAllBanners,
  getBannerById,
  deleteBanner,
  updateBannerStatus,
  reorderBanners,
  getBannerAnalytics,

  // Collections
  createCollection,
  updateCollection,
  getAllCollections,
  getCollectionById,
  deleteCollection,
  updateCollectionStatus,
  getCollectionProducts,

  // Contact Info
  createContactInfo,
  updateContactInfo,
  getAllContactInfo,
  getContactInfoById,
  deleteContactInfo,

  // Feature Flags
  updateFeatureFlags,
  bulkUpdateFeatures,

  // Home Layout
  updateHomeLayout,
  previewHomeLayout,

  // Import/Export
  importBanners,
  exportConfig,

  // A/B Testing
  createExperiment,
  updateExperiment,
  getExperiments,
  getExperimentResults,
};

export default appSettingsService;
